import React, { useState, useEffect } from 'react';
import {
  Card, Button, InputNumber, Space, Typography, Row, Col,
  Statistic, Tag, Divider, List, Popconfirm, Alert, Switch, Table, Input, Tooltip, Grid
} from 'antd';
import {
  PlusOutlined, CheckCircleOutlined, UndoOutlined,
  TrophyOutlined, FireOutlined, HeartFilled, CommentOutlined
} from '@ant-design/icons';

import { getStufeInfo } from '../../api/services/equalOffenseService';

const { Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

/**
 * EqualOffenseTracker Component
 * Main UI for tracking points during an Equal Offense session
 * With per-life point tracking
 */
const EqualOffenseTracker = ({
  session,
  frames = [],
  onAddFrame,
  onDeleteFrame,
  onFinishSession,
  loading = false,
}) => {
  // Responsive breakpoints
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSmall = !screens.sm;

  const stufeInfo = getStufeInfo(session?.stufe);
  const isGodMode = session?.stufe === 7;

  // Anzahl Leben für diese Stufe (1-3, bei God-Mode 0)
  const maxLives = isGodMode ? 1 : (stufeInfo?.value <= 3 ? (4 - stufeInfo.value) : (7 - stufeInfo.value));

  // State für Punkte pro Leben und Fouls pro Leben
  const [lifePoints, setLifePoints] = useState(() => Array(maxLives).fill(0));
  const [lifeFouls, setLifeFouls] = useState(() => Array(maxLives).fill(false));
  const [frameNotes, setFrameNotes] = useState('');

  // Reset lifePoints und lifeFouls wenn sich maxLives ändert
  useEffect(() => {
    setLifePoints(Array(maxLives).fill(0));
    setLifeFouls(Array(maxLives).fill(false));
  }, [maxLives]);

  // Calculate stats
  const totalPoints = session?.total_points || 0;
  const highRun = session?.high_run || 0;
  const totalInnings = frames.length;
  const averagePerInning = totalInnings > 0 ? (totalPoints / totalInnings).toFixed(1) : 0;
  const maxInnings = session?.max_innings || 10;
  const remainingInnings = session?.remaining_innings ?? (maxInnings - totalInnings);

  // Berechne Summe dieser Aufnahme
  const currentInningTotal = lifePoints.reduce((sum, p) => sum + (p || 0), 0);

  // Berechne wie viele Leben verbraucht wurden (Leben mit 0 Punkten nach dem ersten Leben mit Punkten)
  const calculateLivesUsed = () => {
    let lastLifeWithPoints = -1;
    for (let i = lifePoints.length - 1; i >= 0; i--) {
      if (lifePoints[i] > 0) {
        lastLifeWithPoints = i;
        break;
      }
    }
    // Wenn keine Punkte gemacht wurden, wurde mindestens 1 Leben verbraucht
    if (lastLifeWithPoints === -1) return maxLives;
    // Ansonsten: verbrauchte Leben = Index des letzten Lebens mit Punkten
    return lastLifeWithPoints;
  };

  // Kumulierte Summen berechnen
  const runningTotals = [];
  let runningSum = 0;
  for (let i = 0; i < lifePoints.length; i++) {
    runningSum += lifePoints[i] || 0;
    runningTotals.push(runningSum);
  }

  const handleLifePointChange = (index, value) => {
    const newPoints = [...lifePoints];
    newPoints[index] = value || 0;
    setLifePoints(newPoints);
  };

  const handleLifeFoulChange = (index, checked) => {
    const newFouls = [...lifeFouls];
    newFouls[index] = checked;
    setLifeFouls(newFouls);
  };

  const handleAddFrame = () => {
    const livesUsed = calculateLivesUsed();

    // Prüfen ob mindestens ein Foul begangen wurde
    const anyFoul = lifeFouls.some(f => f);

    const frameData = {
      session: session.id,
      player: session.player,
      points: currentInningTotal,
      lives_used: livesUsed,
      life_points: lifePoints, // Punkte pro Leben als Array
      life_fouls: lifeFouls, // Foul pro Leben als Array
      foul: anyFoul, // Legacy-Feld: true wenn irgendein Foul
      notes: frameNotes.trim(), // Kommentar zur Aufnahme
    };
    onAddFrame(frameData);

    // Reset inputs
    setLifePoints(Array(maxLives).fill(0));
    setLifeFouls(Array(maxLives).fill(false));
    setFrameNotes('');
  };

  // Maximale Kugeln pro Aufnahme (bei Stufe 1-6: 15, bei God-Mode unbegrenzt wegen Rebuild)
  const maxBallsPerInning = isGodMode ? 150 : 15;

  // Berechne verfügbare Kugeln für jedes Leben
  const getAvailableBalls = (lifeIndex) => {
    // Summe der Punkte aus vorherigen Leben
    const previousPoints = lifePoints.slice(0, lifeIndex).reduce((sum, p) => sum + (p || 0), 0);
    return Math.max(0, maxBallsPerInning - previousPoints);
  };

  // Quick add buttons - dynamisch basierend auf verfügbaren Kugeln
  const getQuickAddButtons = (lifeIndex) => {
    const available = getAvailableBalls(lifeIndex);
    const buttons = [0];
    for (let i = 1; i <= Math.min(available, 15); i++) {
      buttons.push(i);
    }
    return buttons;
  };

  // Leben-Label
  const getLifeLabel = (index) => {
    if (isGodMode) return 'Punkte';
    const lifeNumber = index + 1;
    return `${lifeNumber}. Leben`;
  };

  return (
    <div>
      {/* Session Header - kompakter auf Mobile */}
      <Card
        style={{ marginBottom: isMobile ? 8 : 16 }}
        bodyStyle={{ padding: isMobile ? '12px 8px' : undefined }}
      >
        {isMobile ? (
          // Mobile: 2x2 Grid, kompakter
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Gesamt</Text>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                <TrophyOutlined style={{ marginRight: 4 }} />{totalPoints}
              </div>
            </div>
            <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>High Run</Text>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#ff4d4f' }}>
                <FireOutlined style={{ marginRight: 4 }} />{highRun}
              </div>
            </div>
            <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Aufnahmen{!isGodMode && ` (${maxInnings})`}
              </Text>
              <div style={{
                fontSize: 20,
                fontWeight: 'bold',
                color: !isGodMode && remainingInnings <= 2 ? '#ff4d4f' : undefined
              }}>
                {totalInnings}{!isGodMode && `/${maxInnings}`}
              </div>
            </div>
            <div style={{ flex: '1 1 45%', textAlign: 'center', padding: '4px 0' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Ø/Aufnahme</Text>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{averagePerInning}</div>
            </div>
          </div>
        ) : (
          // Desktop: Standard Layout
          <Row gutter={16}>
            <Col xs={12} sm={6}>
              <Statistic
                title="Gesamtpunkte"
                value={totalPoints}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="High Run"
                value={highRun}
                prefix={<FireOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title={isGodMode ? "Aufnahmen" : `Aufnahmen (max ${maxInnings})`}
                value={totalInnings}
                suffix={!isGodMode && remainingInnings !== null ? `/ ${maxInnings}` : undefined}
                valueStyle={!isGodMode && remainingInnings <= 2 ? { color: '#ff4d4f' } : undefined}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Statistic
                title="Ø pro Aufnahme"
                value={averagePerInning}
              />
            </Col>
          </Row>
        )}
      </Card>

      {/* Stufe Info - kompakter auf Mobile */}
      <Alert
        type="info"
        showIcon={!isMobile}
        style={{ marginBottom: isMobile ? 8 : 16, padding: isMobile ? '8px 12px' : undefined }}
        message={
          <Space size={isMobile ? 4 : 8} wrap>
            <Tag color={stufeInfo.value <= 3 ? 'green' : stufeInfo.value <= 6 ? 'orange' : 'red'}>
              Stufe {stufeInfo.value}
            </Tag>
            <Text style={{ fontSize: isMobile ? 12 : 14 }}>{stufeInfo.description}</Text>
          </Space>
        }
      />

      {/* Point Input per Life */}
      <Card
        title={
          <Space size={isMobile ? 4 : 8}>
            <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
              Aufnahme {totalInnings + 1}
            </Text>
            <Tag color="blue" style={{ fontSize: isMobile ? 14 : 16 }}>
              Σ {currentInningTotal}
            </Tag>
          </Space>
        }
        style={{ marginBottom: isMobile ? 8 : 16 }}
        bodyStyle={{ padding: isMobile ? 8 : 16 }}
      >
        {isMobile ? (
          // Mobile: Vertikale Karten
          <Row gutter={[8, 8]}>
            {lifePoints.map((points, index) => {
              const availableBalls = getAvailableBalls(index);
              const quickButtons = getQuickAddButtons(index);
              const isExhausted = availableBalls === 0;

              return (
                <Col xs={24} key={index}>
                  <Card
                    size="small"
                    style={{
                      background: isExhausted ? '#fff1f0' : points > 0 ? '#f6ffed' : '#fafafa',
                      borderColor: isExhausted ? '#ffa39e' : points > 0 ? '#52c41a' : '#d9d9d9',
                      opacity: isExhausted ? 0.7 : 1,
                    }}
                    bodyStyle={{ padding: '8px 12px' }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 80 }}>
                          <HeartFilled style={{ color: '#ff4d4f', fontSize: 14 }} />
                          <Text strong style={{ fontSize: 13 }}>{getLifeLabel(index)}</Text>
                        </div>
                        <InputNumber
                          value={points}
                          onChange={(val) => handleLifePointChange(index, Math.min(val || 0, availableBalls))}
                          min={0}
                          max={availableBalls}
                          size="middle"
                          style={{ width: 70 }}
                          disabled={isExhausted}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>Σ {runningTotals[index]}</Text>
                        {!isGodMode && (
                          <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>max {availableBalls}</Text>
                        )}
                      </div>
                      {!isExhausted && (
                        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 8 }}>
                          {quickButtons.map(p => (
                            <Button
                              key={p}
                              size="small"
                              type={points === p ? 'primary' : 'default'}
                              onClick={() => handleLifePointChange(index, p)}
                              style={{ minWidth: 32, padding: '0 6px', fontSize: 13 }}
                            >
                              {p}
                            </Button>
                          ))}
                        </div>
                      )}
                      {isExhausted && (
                        <div style={{ marginBottom: 8 }}>
                          <Tag color="green" style={{ fontSize: 11 }}>Alle versenkt!</Tag>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, borderTop: '1px dashed #d9d9d9' }}>
                        <Switch size="small" checked={lifeFouls[index]} onChange={(checked) => handleLifeFoulChange(index, checked)} />
                        <Text type={lifeFouls[index] ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>Foul</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          // Desktop: Horizontales kompaktes Layout - alle Leben nebeneinander
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            {lifePoints.map((points, index) => {
              const availableBalls = getAvailableBalls(index);
              const quickButtons = getQuickAddButtons(index);
              const isExhausted = availableBalls === 0;

              return (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderRadius: 8,
                    background: isExhausted ? '#fff1f0' : points > 0 ? '#f6ffed' : '#fafafa',
                    border: `1px solid ${isExhausted ? '#ffa39e' : points > 0 ? '#52c41a' : '#d9d9d9'}`,
                    opacity: isExhausted ? 0.7 : 1,
                  }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Space size={4}>
                      <HeartFilled style={{ color: '#ff4d4f' }} />
                      <Text strong>{getLifeLabel(index)}</Text>
                    </Space>
                    {!isGodMode && (
                      <Text type="secondary" style={{ fontSize: 11 }}>max {availableBalls}</Text>
                    )}
                  </div>

                  {/* Input + Running Total */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <InputNumber
                      value={points}
                      onChange={(val) => handleLifePointChange(index, Math.min(val || 0, availableBalls))}
                      min={0}
                      max={availableBalls}
                      size="middle"
                      style={{ width: 70 }}
                      disabled={isExhausted}
                    />
                    <Tag color="blue" style={{ margin: 0 }}>Σ {runningTotals[index]}</Tag>
                  </div>

                  {/* Quick Buttons - kompakt in 2 Reihen (0-7 und 8-15) */}
                  {!isExhausted && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                        {quickButtons.filter(p => p <= 7).map(p => (
                          <Button
                            key={p}
                            size="small"
                            type={points === p ? 'primary' : 'default'}
                            onClick={() => handleLifePointChange(index, p)}
                            style={{ flex: 1, minWidth: 0, padding: '0 2px', fontSize: 11 }}
                          >
                            {p}
                          </Button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {quickButtons.filter(p => p > 7).map(p => (
                          <Button
                            key={p}
                            size="small"
                            type={points === p ? 'primary' : 'default'}
                            onClick={() => handleLifePointChange(index, p)}
                            style={{ flex: 1, minWidth: 0, padding: '0 2px', fontSize: 11 }}
                          >
                            {p}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {isExhausted && (
                    <Tag color="green" style={{ marginBottom: 8 }}>Alle versenkt!</Tag>
                  )}

                  {/* Foul Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8, borderTop: '1px dashed #d9d9d9' }}>
                    <Switch size="small" checked={lifeFouls[index]} onChange={(checked) => handleLifeFoulChange(index, checked)} />
                    <Text type={lifeFouls[index] ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>Foul</Text>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Kommentar-Feld */}
        <div style={{ marginTop: isMobile ? 8 : 16 }}>
          <Space style={{ marginBottom: 4 }}>
            <CommentOutlined />
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              Kommentar (optional)
            </Text>
          </Space>
          <TextArea
            value={frameNotes}
            onChange={(e) => setFrameNotes(e.target.value)}
            placeholder="Übungshinweise, besondere Situationen..."
            autoSize={{ minRows: 1, maxRows: isMobile ? 2 : 3 }}
            size={isMobile ? 'middle' : 'large'}
          />
        </div>

        <Divider style={{ margin: isMobile ? '12px 0' : '24px 0' }} />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddFrame}
          loading={loading}
          size={isMobile ? 'middle' : 'large'}
          block
        >
          Aufnahme speichern ({currentInningTotal} Punkte)
        </Button>
      </Card>

      {/* Frame History with Running Total */}
      <Card
        title={
          <span style={{ fontSize: isMobile ? 14 : 16 }}>
            Aufnahmen ({frames.length})
          </span>
        }
        extra={
          <Popconfirm
            title="Session beenden?"
            description="Möchtest du diese Session wirklich beenden?"
            onConfirm={() => onFinishSession()}
            okText="Beenden"
            cancelText="Abbrechen"
          >
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              disabled={frames.length === 0}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? 'Beenden' : 'Session beenden'}
            </Button>
          </Popconfirm>
        }
        bodyStyle={{ padding: isMobile ? 8 : undefined }}
      >
        {frames.length === 0 ? (
          <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
            Noch keine Aufnahmen. Starte mit deiner ersten Aufnahme!
          </Text>
        ) : isMobile ? (
          // Mobile: Kompakte Liste statt Tabelle
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[...frames].map((frame, idx) => {
              const runningTotal = frames
                .slice(0, idx + 1)
                .reduce((sum, f) => sum + f.points, 0);
              const lifeFouls = frame.life_fouls || [];
              const foulCount = lifeFouls.filter(f => f).length;

              return (
                <div
                  key={frame.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    background: '#fafafa',
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                >
                  <Tag color="default" style={{ margin: 0, fontSize: 12 }}>
                    #{frame.frame_number}
                  </Tag>
                  <Text strong style={{ color: frame.points >= 10 ? '#52c41a' : undefined }}>
                    {frame.points}
                  </Text>
                  <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                    Σ{runningTotal}
                  </Tag>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', alignItems: 'center' }}>
                    {frame.lives_used > 0 && (
                      <Tag color="red" style={{ margin: 0, fontSize: 10 }}>-{frame.lives_used}</Tag>
                    )}
                    {foulCount > 0 && (
                      <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>{foulCount}F</Tag>
                    )}
                    {frame.notes && (
                      <Tooltip title={frame.notes}>
                        <CommentOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                      </Tooltip>
                    )}
                    <Popconfirm
                      title="Aufnahme löschen?"
                      onConfirm={() => onDeleteFrame(frame.id)}
                      okText="Ja"
                      cancelText="Nein"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<UndoOutlined />}
                        size="small"
                        style={{ padding: '0 4px' }}
                      />
                    </Popconfirm>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Desktop: Standard-Tabelle
          <Table
            dataSource={[...frames].map((frame, idx) => {
              // Kumulierte Summe berechnen
              const runningTotal = frames
                .slice(0, idx + 1)
                .reduce((sum, f) => sum + f.points, 0);
              return { ...frame, runningTotal, key: frame.id };
            })}
            columns={[
              {
                title: '#',
                dataIndex: 'frame_number',
                key: 'frame_number',
                width: 50,
                render: (num) => (
                  <Tag color="default">#{num}</Tag>
                ),
              },
              {
                title: 'Punkte',
                dataIndex: 'points',
                key: 'points',
                render: (points) => (
                  <Text strong style={{ color: points >= 10 ? '#52c41a' : undefined }}>
                    {points}
                  </Text>
                ),
              },
              {
                title: 'Σ Gesamt',
                dataIndex: 'runningTotal',
                key: 'runningTotal',
                render: (total) => (
                  <Tag color="blue">{total}</Tag>
                ),
              },
              {
                title: 'Status',
                key: 'status',
                render: (_, record) => {
                  const lifeFouls = record.life_fouls || [];
                  const foulCount = lifeFouls.filter(f => f).length;
                  return (
                    <Space size={4}>
                      {record.lives_used > 0 && <Tag color="red">-{record.lives_used} Leben</Tag>}
                      {foulCount > 0 && (
                        <Tag color="orange">
                          {foulCount}x Foul
                        </Tag>
                      )}
                      {record.notes && (
                        <Tooltip title={record.notes}>
                          <CommentOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      )}
                    </Space>
                  );
                },
              },
              {
                title: '',
                key: 'action',
                width: 80,
                render: (_, record) => (
                  <Popconfirm
                    title="Aufnahme löschen?"
                    onConfirm={() => onDeleteFrame(record.id)}
                    okText="Ja"
                    cancelText="Nein"
                  >
                    <Button type="text" danger icon={<UndoOutlined />} size="small" />
                  </Popconfirm>
                ),
              },
            ]}
            pagination={false}
            size="small"
          />
        )}
      </Card>
    </div>
  );
};

export default EqualOffenseTracker;
